import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Container,
    FormControl,
    FormLabel,
    TextField,
    Typography,
    Alert,
    Grid,
    Chip,
    Stack
} from '@mui/material';
import {useSelector, useDispatch} from "react-redux";
import {RootState} from "../../../store/store";
import {updateUser} from "../../../store/slices/session_slice";
import {WebClient} from "../../../store/clients/client";
import {User} from "../../../models/user";

export default function UserSettings() {
    const dispatch = useDispatch();
    const userData = useSelector((state: RootState) => state.session.user);
    
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState('');
    
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState(false);
    const [passwordErrorMsg, setPasswordErrorMsg] = useState('');

    useEffect(() => {
        loadUserData();
    }, []);

    useEffect(() => {
        if (userData && Object.keys(userData).length > 0) {
            setEmail(userData.email || userData.username || '');
            setFirstName(userData.first_name || '');
            setLastName(userData.last_name || '');
            setIsLoadingUser(false);
        } else {
            loadUserData();
        }
    }, [userData]);

    const loadUserData = async () => {
        setIsLoadingUser(true);
        try {
            const response = await WebClient.GetUserDetails();
            if (response.status === 200) {
                const user = response.data as User;
                setEmail(user.email || user.username || '');
                setFirstName(user.first_name || '');
                setLastName(user.last_name || '');
                dispatch(updateUser(user));
            }
        } catch (error) {
            console.error('Error loading user data:', error);
        } finally {
            setIsLoadingUser(false);
        }
    };

    const validateEmail = (emailValue: string): boolean => {
        // More robust email validation regex
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return emailRegex.test(emailValue);
    };

    const validateUserInfo = () => {
        let isValid = true;

        if (!email || email.trim().length === 0) {
            setEmailError(true);
            setEmailErrorMessage('Email is required.');
            isValid = false;
        } else if (!validateEmail(email.trim())) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        return isValid;
    };

    const validatePassword = () => {
        let isValid = true;
        const errors: string[] = [];

        if (!password) {
            setPasswordError(true);
            setPasswordErrorMessage('Password is required.');
            isValid = false;
            return isValid;
        }

        // Check minimum length
        if (password.length < 8) {
            errors.push('at least 8 characters');
        }

        // Check for uppercase letter
        if (!/[A-Z]/.test(password)) {
            errors.push('one uppercase letter');
        }

        // Check for lowercase letter
        if (!/[a-z]/.test(password)) {
            errors.push('one lowercase letter');
        }

        // Check for number
        if (!/[0-9]/.test(password)) {
            errors.push('one number');
        }

        // Check for special character
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
            errors.push('one special character');
        }

        if (errors.length > 0) {
            setPasswordError(true);
            setPasswordErrorMessage(`Password must contain ${errors.join(', ')}.`);
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError(true);
            setConfirmPasswordErrorMessage('Passwords do not match.');
            isValid = false;
        } else {
            setConfirmPasswordError(false);
            setConfirmPasswordErrorMessage('');
        }

        return isValid;
    };

    const handleUserInfoSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitSuccess(false);
        setSubmitError('');

        if (!validateUserInfo()) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await WebClient.ModifyUser(
                email.trim(),
                email.trim(),
                firstName.trim(),
                lastName.trim()
            );

            if (response.status === 200) {
                setSubmitSuccess(true);
                // Reload user data to get updated info
                await loadUserData();
                setTimeout(() => setSubmitSuccess(false), 3000);
            } else {
                setSubmitError(response.data?.error || 'Failed to update user information. Please try again.');
            }
        } catch (error) {
            setSubmitError('An error occurred while updating user information.');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordSuccess(false);
        setPasswordErrorMsg('');

        if (!validatePassword()) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await WebClient.ChangePassword(password);

            if (response.status === 200) {
                setPasswordSuccess(true);
                setPassword('');
                setConfirmPassword('');
                setTimeout(() => setPasswordSuccess(false), 3000);
            } else {
                setPasswordErrorMsg(response.data?.error || 'Failed to change password. Please try again.');
            }
        } catch (error) {
            setPasswordErrorMsg('An error occurred while changing password.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoadingUser) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container sx={{ py: 2 }}>
            <Box sx={{ maxWidth: { sm: '100%', md: '800px' }, mx: 'auto' }}>
                <Typography component="h2" variant="h5" gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                    User Settings
                </Typography>

                {/* User Information Section */}
                <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                        User Information
                    </Typography>

                    {submitSuccess && (
                        <Alert severity="success" sx={{ mb: 1.5 }}>
                            User information updated successfully!
                        </Alert>
                    )}

                    {submitError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {submitError}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleUserInfoSubmit} noValidate>
                        <FormControl fullWidth sx={{ mb: 1.5 }}>
                            <FormLabel htmlFor="email">Email</FormLabel>
                            <TextField
                                id="email"
                                name="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={emailError}
                                helperText={emailErrorMessage}
                                required
                                fullWidth
                                size="small"
                                variant="outlined"
                                color={emailError ? 'error' : 'primary'}
                                disabled={isLoading}
                            />
                        </FormControl>

                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor="first_name">First Name</FormLabel>
                                    <TextField
                                        id="first_name"
                                        name="first_name"
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        disabled={isLoading}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor="last_name">Last Name</FormLabel>
                                    <TextField
                                        id="last_name"
                                        name="last_name"
                                        type="text"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        disabled={isLoading}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={isLoading}
                            size="small"
                        >
                            {isLoading ? 'Updating...' : 'Update User Information'}
                        </Button>
                    </Box>
                </Card>

                {/* User Groups Section */}
                {userData?.groups && userData.groups.length > 0 && (
                    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                            User Groups
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ gap: 0.75 }}>
                            {userData.groups.map((group, index) => (
                                <Chip
                                    key={index}
                                    label={group.name}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        fontWeight: 500
                                    }}
                                />
                            ))}
                        </Stack>
                    </Card>
                )}

                {/* Password Change Section */}
                <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                        Change Password
                    </Typography>

                    {passwordSuccess && (
                        <Alert severity="success" sx={{ mb: 1.5 }}>
                            Password changed successfully!
                        </Alert>
                    )}

                    {passwordErrorMsg && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {passwordErrorMsg}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor="password">New Password</FormLabel>
                                    <TextField
                                        id="password"
                                        name="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        error={passwordError}
                                        helperText={passwordErrorMessage}
                                        required
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        color={passwordError ? 'error' : 'primary'}
                                        disabled={isLoading}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor="confirm_password">Confirm New Password</FormLabel>
                                    <TextField
                                        id="confirm_password"
                                        name="confirm_password"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        error={confirmPasswordError}
                                        helperText={confirmPasswordErrorMessage}
                                        required
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        color={confirmPasswordError ? 'error' : 'primary'}
                                        disabled={isLoading}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={isLoading}
                            size="small"
                        >
                            {isLoading ? 'Changing Password...' : 'Change Password'}
                        </Button>
                    </Box>
                </Card>
            </Box>
        </Container>
    );
}
